import { getLocalBookingDateString } from "./BookingDateUtils";
import { filterBookingsForSaleAccess, isBookingSaleScopedUser } from "./BookingSaleAccess";
import { filterRecordsByStaffCustomerAccess } from "../../../utils/staffCustomerAccess";

export const parseScheduleShifts = (value) => {
  const text = String(value || "").toUpperCase().trim();
  if (text === "OFF") return ["OFF"];
  return ["D", "N"].filter((shift) => text.includes(shift));
};

// Match the booking calendar's visibility before exposing destinations.
export const filterScheduleBookings = (records, currentUser, nodes, adminData) => {
  const roles = currentUser.roles || [];
  const codes = Array.isArray(adminData.node) ? adminData.node : [adminData.node];
  const allowedNodes = !isBookingSaleScopedUser(currentUser) && roles.some((role) =>
    ["super_admin", "accountant", "sleep_physician", "sleep_technician"].includes(role)
  ) ? nodes : nodes.filter((node) => codes.includes(node.nodecode));
  const allowedNames = new Set(allowedNodes.map((node) => node.nodename));
  const visible = records.filter(({ extendedProps: props = {} }) =>
    allowedNames.has(props.node) &&
    (!roles.includes("sleep_physician") || props.doctor === currentUser.displayName) &&
    (!roles.includes("sleep_technician") ||
      props.techrecord === currentUser.displayName || props.technician === currentUser.displayName)
  );
  return filterRecordsByStaffCustomerAccess({
    records: filterBookingsForSaleAccess({ records: visible, currentUser, assignedNodes: allowedNodes }),
    currentUser,
    adminData,
    nodes: allowedNodes,
    getNodeValue: (record) => record.extendedProps.node,
    getCustomerValue: (record) => record.extendedProps.customer,
  });
};

export const buildScheduleAssignments = (bookings, year, month, nodes = []) => {
  const result = {};
  const caseCounts = {};
  const seenBookings = new Set();
  const monthPrefix = `${year}-${String(month + 1).padStart(2, "0")}-`;
  const clean = (value) => {
    const text = String(value || "").trim();
    return ["กรุณาเลือก", "-"].includes(text) ? "" : text;
  };
  const nameKey = (value) => clean(value).replace(/\s+/g, " ").toLocaleLowerCase();
  const nodeLookup = new Map();
  nodes.forEach((node) => {
    [node.nodename, node.nodecode].forEach((name) => {
      if (clean(name)) nodeLookup.set(nameKey(name), node);
    });
  });
  bookings.forEach((booking) => {
    const props = booking.extendedProps || {};
    const technician = clean(props.techrecord);
    const studyDate = props.latestSleepStudyDate;
    if (!technician || !studyDate) return;
    // A case can progress to another status/event date after the sleep test.
    // Its recording assignment still belongs to the saved study date.
    // Use the study date exclusively, even when the booking calendar start differs.
    // Timestamp dates use Bangkok time, matching the booking calendar.
    let date;
    if (typeof studyDate.toDate === "function" || studyDate instanceof Date) {
      const value = studyDate instanceof Date ? studyDate : studyDate.toDate();
      if (Number.isNaN(value.getTime())) return;
      date = new Date(value.getTime() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
    } else {
      if (typeof studyDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(studyDate)) return;
      date = getLocalBookingDateString(studyDate);
      if (date !== studyDate) return;
    }
    if (!date.startsWith(monthPrefix)) return;
    // The loader supplies the canonical document path as id. Count each booking
    // once, while keeping separate cases at the same location distinct.
    if (booking.id && seenBookings.has(booking.id)) return;
    if (booking.id) seenBookings.add(booking.id);
    const key = `${technician}-${Number(date.slice(8, 10))}`;
    caseCounts[key] = (caseCounts[key] || 0) + 1;
    const node = clean(props.node);
    const customer = clean(props.customer);
    const nodeDetails = nodeLookup.get(nameKey(node)) || {};
    const shortNode = clean(nodeDetails.nodecode) || node;
    const sameCustomer = [node, nodeDetails.nodename, nodeDetails.nodecode]
      .some((name) => nameKey(name) === nameKey(customer));
    const destination = [shortNode, !sameCustomer ? customer : ""].filter(Boolean).join(" / ") || "Site not specified";
    if (!result[key]) result[key] = [];
    if (!result[key].includes(destination)) result[key].push(destination);
  });
  return { destinations: result, caseCounts };
};

export const buildScheduleDestinations = (bookings, year, month, nodes = []) =>
  buildScheduleAssignments(bookings, year, month, nodes).destinations;
